"use client";

import { cn } from "@universe/ui/lib/utils";
import { format } from "date-fns";
import { useTranslations } from "next-intl";
import { useMemo } from "react";

import LedgerEntryGroup from "@/components/ledger/ledger-entry-group";
import LedgerListTransition from "@/components/ledger/ledger-list-transition";
import RefreshButton from "@/components/refresh-button";
import { LEDGER_ENTRY_GROUP_HEADER_DATE_FORMAT } from "@/constants/dateTime";
import { useListItemReveal } from "@/hooks/useListItemReveal";
import { useMeasuredHeight } from "@/hooks/useMeasuredHeight";
import { useRefreshAllLists } from "@/hooks/useRefreshAllLists";
import { useCustomerAccount, useDashboardHeader } from "@/providers";
import { useGetProcessingPaymentsQuery } from "@generated/redux/services/generated/api";

import StickyHeader from "../sticky-header";

import useGroupedPaymentsList, {
  type PaymentListItem,
} from "./hooks/useGroupedPaymentsList";
import PaymentDrawerDetails from "./payment-drawer-details";
import PaymentItem from "./payment-item";

export type ProcessingItem = PaymentListItem;

interface ProcessingPaymentsListProps {
  className?: string;
  filterCurrency?: string[];
}

export default function ProcessingPaymentsList({
  className,
  filterCurrency,
}: ProcessingPaymentsListProps) {
  const tPayments = useTranslations("payments");
  const { customerAccount } = useCustomerAccount();
  const { refreshProcessingList } = useRefreshAllLists();
  const { listHeaderTop, paymentsDateGroupTop, setListHeaderHeight } =
    useDashboardHeader();

  const headerRef = useMeasuredHeight(setListHeaderHeight);

  const {
    data: paymentsData,
    isLoading,
    isFetching,
  } = useGetProcessingPaymentsQuery(
    {
      filterCustomerAccount: [customerAccount!.customerAccount!.id!],
      ...(filterCurrency?.length ? { filterCurrency } : {}),
    },
    {
      skip: !customerAccount?.customerAccount?.id,
    }
  );

  const {
    isEmpty,
    isInitialLoad,
    isLoadingOrFetching,
    selectedItem,
    selectedIndex,
    groupedItems,
    handleOnClose,
    selectItem,
  } = useGroupedPaymentsList({
    queryData: paymentsData,
    isLoading,
    isFetching,
  });

  const itemKeys = useMemo(
    () => groupedItems.flatMap(([, items]) => items.map((item) => item.id)),
    [groupedItems]
  );
  const revealingItemDelays = useListItemReveal(
    itemKeys,
    isInitialLoad,
    isEmpty
  );

  return (
    <div className={cn("flex relative", className)}>
      <div className="w-full relative">
        <StickyHeader
          ref={headerRef}
          subHeader={3}
          noPageHeader
          className="flex flex-col gap-4 lg:gap-3 justify-between"
          style={{
            top: `calc(${listHeaderTop}px + var(--s21-banner-height, 0px))`,
          }}
        >
          <div className="mb-0">
            <div className="flex items-center gap-3">
              <h2 className="text-lg md:text-xl font-semibold">
                {tPayments("processingPayments")}
              </h2>
              <RefreshButton
                isLoading={isLoadingOrFetching}
                onRefresh={refreshProcessingList}
              />
            </div>
            <p className="text-sm text-gray-700 mt-2 mb-2.5">
              {tPayments("processingPaymentsExplainer")}
            </p>
          </div>
        </StickyHeader>
        <div className="mx-[-12px] relative">
          <LedgerListTransition
            isInitialLoad={isInitialLoad}
            isEmpty={isEmpty}
            emptyMessage={tPayments("noProcessingPayments")}
          >
            <LedgerEntryGroup<ProcessingItem & { id: string }>
              dateGroupTop={paymentsDateGroupTop}
              revealingItemDelays={revealingItemDelays}
              groupedList={
                groupedItems as [string, (ProcessingItem & { id: string })[]][]
              }
              renderGroupTitle={(date) =>
                format(new Date(date), LEDGER_ENTRY_GROUP_HEADER_DATE_FORMAT)
              }
              renderGroupItem={(item, index) => (
                <PaymentItem
                  key={item.id}
                  index={index}
                  payment={item}
                  selectedIndex={selectedIndex}
                  setSelectedPayment={() => selectItem(item, index)}
                />
              )}
            />
          </LedgerListTransition>
        </div>
      </div>

      <PaymentDrawerDetails
        payment={selectedItem}
        onClose={handleOnClose}
        showActionsTab={false}
        statusOverride="SCHEDULED"
      />
    </div>
  );
}
